import cn from 'classnames';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import OpCard from 'components/Common/op-ui-wrappers/OpCard/OpCard';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import useClickOutside from 'hooks/useClickOutside';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';
import styles from './BaseSelector.module.scss';

/**
 * Base selector component props
 * @param type - simple is used for Paper thickness, foils and cardstock, complex is used for frames and envelopes
 */
export interface BaseSelectorProps {
  thumbnail: string;
  title: string;
  actionString: string;
  buttonHeading: string;
  selectorApplied: boolean;
  removeSelector: (event: React.MouseEvent<HTMLDivElement>) => void;
  applySelector: () => void;
  type: 'simple' | 'complex';
  theme?: 'foil' | 'frame' | 'envelope' | 'cardstock' | 'paperThickness';
  onContentOpen?: () => void;
}

const BaseSelector: FC<BaseSelectorProps> = ({
  title,
  actionString,
  buttonHeading,
  selectorApplied,
  removeSelector,
  applySelector,
  children,
  type,
  theme,
  onContentOpen,
}) => {
  const [open, setOpen] = useState(false);
  const isDesktopDevice = useSelector(isDesktop);
  const optionsRef = React.createRef<HTMLDivElement>();
  const buttonRef = React.createRef<HTMLDivElement>();
  const { t } = useTranslation();

  useClickOutside(
    optionsRef,
    () => {
      setOpen(false);
    },
    buttonRef,
  );

  const handleApplySelector = () => {
    applySelector();
    setOpen(false);
  };

  const handleOnOpen = () => {
    setOpen(!open);
    if (onContentOpen) {
      onContentOpen();
    }
  };

  return (
    <div className={styles.container}>
      {type === 'simple' && (
        <div
          ref={buttonRef}
          className={cn(styles.buttonSimple, styles[`button-simple-${theme}`])}
          onClick={handleOnOpen}
        >
          <div className={styles.buttonSimpleText}>{buttonHeading}</div>
        </div>
      )}
      {type === 'complex' && (
        <div
          onClick={handleOnOpen}
          className={cn(styles.button, {
            [styles.greenBorder]: selectorApplied,
          })}
          ref={buttonRef}
        >
          <div className={styles.thumbnail} />
          <div className={styles.text}>
            <div className={styles.buttonHeading}>
              <OpText variant="body" size="medium">
                {buttonHeading}
              </OpText>
            </div>
            <div className={styles.buttonDescription}>{t('txt_select_options')}</div>
          </div>
          {selectorApplied && (
            <div onClick={removeSelector}>
              <OpIcon icon="trash" size="small" />
            </div>
          )}
        </div>
      )}
      {open && (
        <OpCard ref={optionsRef}>
          {!isDesktopDevice && (
            <div className={styles.popupTitle}>
              <div className={styles.popupTitleText}>
                <OpText size="large" variant="body">
                  {title}
                </OpText>
              </div>
              <div className={styles.popupTitleClose} onClick={() => setOpen(false)}>
                <OpIcon icon="close" size="small" />
              </div>
            </div>
          )}
          <div className={styles.options}>
            {isDesktopDevice && (
              <div className={cn(styles.row, styles.header)}>
                <div
                  className={cn(styles.row, styles.headerTitle)}
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <OpText size="medium" variant="body">
                    {t('txt_select_options')}
                  </OpText>
                  <OpIcon icon="dropdown-arrow-up" size="small" />
                </div>
                <div className={styles.applyButton} onClick={handleApplySelector}>
                  <OpButton color="action" variant="primary" size="small">
                    {actionString}
                  </OpButton>
                </div>
              </div>
            )}
            {children}
          </div>
          {!isDesktopDevice && (
            <div className={styles.popupActions}>
              <div onClick={() => setOpen(false)}>
                <OpButton color="action" variant="secondary" size="large">
                  {t('txt_cancel')}
                </OpButton>
              </div>
              <OpButton color="action" variant="primary" size="large">
                {actionString}
              </OpButton>
            </div>
          )}
        </OpCard>
      )}
    </div>
  );
};

export default BaseSelector;
